'use client';

import { useCallback, useRef, useState } from 'react';
import Image from 'next/image';

import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/icons/ChevronRightIcon';
import { Button } from '@/components';
import MediaPlaceholder from '@/components/ui/MediaPlaceholder';
import { cmsImageUnoptimized, resolveCmsImageSrc } from '@/lib/cms-image-url';

/** Same carousel/grid behavior as `EventHighlightsSection` (homepage event highlights). */
const MAX_SLIDES = 4;
const PER_SLIDE = 4;

const ASPECT_WIDE = 'aspect-[787/343]';
const ASPECT_NARROW = 'aspect-[557/343]';

const MOBILE_LAYOUT = [
  { key: 'wide-1', aspect: ASPECT_WIDE, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
  { key: 'narrow-1', aspect: ASPECT_NARROW, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
  { key: 'narrow-2', aspect: ASPECT_NARROW, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
  { key: 'wide-2', aspect: ASPECT_WIDE, widthClassName: 'w-[242px]', heightClassName: 'h-[242px]' },
] as const;

function padSlide(urls: string[]): string[] {
  const row = urls.slice(0, PER_SLIDE);
  while (row.length < PER_SLIDE) row.push('');
  return row;
}

function buildSlides(urls: string[]): string[][] {
  if (!urls.length) return [];
  const slides: string[][] = [];
  for (let i = 0; i < urls.length && slides.length < MAX_SLIDES; i += PER_SLIDE) {
    slides.push(padSlide(urls.slice(i, i + PER_SLIDE)));
  }
  return slides;
}

function VibeImage({ src, priority, sizes }: { src: string; priority?: boolean; sizes: string }) {
  const resolved = resolveCmsImageSrc(src, src);
  if (!resolved) return <MediaPlaceholder />;
  return (
    <Image
      src={resolved}
      alt=""
      fill
      className="object-cover"
      sizes={sizes}
      priority={priority}
      unoptimized={cmsImageUnoptimized(resolved)}
    />
  );
}

function VibeMobileCard({
  src,
  aspect,
  widthClassName,
  heightClassName,
}: {
  src: string;
  aspect: string;
  widthClassName: string;
  heightClassName: string;
}) {
  return (
    <div
      className={`relative ${widthClassName} ${heightClassName} shrink-0 overflow-hidden rounded-2xl bg-neutral-10 sm:rounded-2xl ${aspect}`}
    >
      <VibeImage src={src} sizes="(max-width: 640px) 80vw, 282px" />
    </div>
  );
}

function VibesSlideGrid({ images, slideIndex }: { images: string[]; slideIndex: number }) {
  const img0 = images[0] ?? '';
  const img1 = images[1] ?? '';
  const img2 = images[2] ?? '';
  const img3 = images[3] ?? '';
  return (
    <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-12">
      <div className={`relative w-full overflow-hidden rounded-2xl bg-neutral-10 sm:rounded-2xl ${ASPECT_WIDE} lg:col-span-7`}>
        <VibeImage src={img0} priority={slideIndex === 0} sizes="(max-width: 1024px) 100vw, 58vw" />
      </div>
      <div className={`relative w-full overflow-hidden rounded-2xl bg-neutral-10 sm:rounded-2xl ${ASPECT_NARROW} lg:col-span-5`}>
        <VibeImage src={img1} sizes="(max-width: 1024px) 100vw, 42vw" />
      </div>
      <div className={`relative w-full overflow-hidden rounded-2xl bg-neutral-10 sm:rounded-2xl ${ASPECT_NARROW} lg:col-span-5`}>
        <VibeImage src={img2} sizes="(max-width: 1024px) 100vw, 42vw" />
      </div>
      <div className={`relative w-full overflow-hidden rounded-2xl bg-neutral-10 sm:rounded-2xl ${ASPECT_WIDE} lg:col-span-7`}>
        <VibeImage src={img3} sizes="(max-width: 1024px) 100vw, 58vw" />
      </div>
    </div>
  );
}

export type EventPastVibesSectionProps = {
  title?: string | null;
  intro?: string | null;
  imageUrls: string[];
};

const DEFAULT_TITLE = 'Past Vibes';

export function EventPastVibesSection({ title, intro, imageUrls }: EventPastVibesSectionProps) {
  const slides = buildSlides(imageUrls.filter((u) => u.trim()));
  const [slideIndex, setSlideIndex] = useState(0);
  const mobileScrollerRef = useRef<HTMLDivElement | null>(null);
  const mobileItemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const slideCount = slides.length;
  const atStart = slideIndex <= 0;
  const atEnd = slideIndex >= Math.max(0, slideCount - 1);

  const scrollToMobileItem = useCallback((index: number) => {
    const container = mobileScrollerRef.current;
    const item = mobileItemRefs.current[index];
    if (!container || !item) return;
    container.scrollTo({
      left: item.offsetLeft - container.offsetLeft,
      behavior: 'smooth',
    });
  }, []);

  const updateMobileIndexFromScroll = useCallback(() => {
    const container = mobileScrollerRef.current;
    if (!container) return;
    const closestIndex = mobileItemRefs.current.reduce((bestIndex, item, index) => {
      if (!item) return bestIndex;
      const currentDistance = Math.abs(item.offsetLeft - container.scrollLeft);
      const bestItem = mobileItemRefs.current[bestIndex];
      const bestDistance = bestItem
        ? Math.abs(bestItem.offsetLeft - container.scrollLeft)
        : Number.POSITIVE_INFINITY;
      return currentDistance < bestDistance ? index : bestIndex;
    }, 0);
    setSlideIndex(closestIndex);
  }, []);

  const handleStep = useCallback(
    (direction: 'prev' | 'next') => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      setSlideIndex((current) => {
        const nextIndex =
          direction === 'prev' ? Math.max(0, current - 1) : Math.min(slideCount - 1, current + 1);
        if (isMobile) scrollToMobileItem(nextIndex);
        return nextIndex;
      });
    },
    [scrollToMobileItem, slideCount]
  );

  if (slideCount === 0) {
    return null;
  }

  const heading = (title && title.trim()) || DEFAULT_TITLE;
  const navBtnClass =
    'inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-15 bg-blue-5 text-blue-80 transition hover:border-blue-80/30 hover:bg-blue-10 hover:text-blue-100 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <section className="relative" aria-labelledby="event-past-vibes-heading">
      <div className="w-full px-4 pb-8 pt-6 sm:px-6 sm:pb-12 sm:pt-8 lg:px-8">
        <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-start">
          <h2
            id="event-past-vibes-heading"
            className={`text-h3-mobile font-bold leading-tight text-blue-100 sm:text-h3-desktop ${intro?.trim() ? 'lg:col-span-7' : 'lg:col-span-12'}`}
          >
            {heading}
          </h2>
          {intro?.trim() ? (
            <p className="text-body-14-desktop font-medium leading-relaxed text-text-secondary sm:text-body-16-desktop lg:col-span-5 lg:text-right">
              {intro.trim()}
            </p>
          ) : null}
        </div>

        <div
          ref={mobileScrollerRef}
          className="scrollbar-hide overflow-x-auto sm:hidden"
          onScroll={updateMobileIndexFromScroll}
        >
          <div className="flex w-max gap-3">
            {slides.map((slideImages, index) => (
              <div
                key={`m-${index}`}
                ref={(element) => {
                  mobileItemRefs.current[index] = element;
                }}
              >
                <div className="flex w-[242px] flex-col gap-3">
                  {MOBILE_LAYOUT.map((item, cellIdx) => (
                    <VibeMobileCard
                      key={`slide-${index}-${item.key}`}
                      src={slideImages[cellIdx] ?? ''}
                      aspect={item.aspect}
                      widthClassName={item.widthClassName}
                      heightClassName={item.heightClassName}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden overflow-hidden sm:block">
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{
              width: `${slideCount * 100}%`,
              transform: `translateX(-${(100 / slideCount) * slideIndex}%)`,
            }}
          >
            {slides.map((slideImages, i) => (
              <div key={`d-${i}`} style={{ width: `${100 / slideCount}%` }} className="shrink-0 pr-1">
                <VibesSlideGrid slideIndex={i} images={slideImages} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-neutral-15" />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Previous slide"
            disabled={atStart}
            onClick={() => handleStep('prev')}
            className={navBtnClass}
          >
            <ChevronLeftIcon size={16} color="currentColor" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Next slide"
            disabled={atEnd}
            onClick={() => handleStep('next')}
            className={navBtnClass}
          >
            <ChevronRightIcon size={16} color="currentColor" />
          </Button>
          <div className="h-px flex-1 bg-neutral-15" />
        </div>
      </div>
    </section>
  );
}
